import { useState, useRef, useEffect, useCallback } from "react";
import { Mic, MicOff, X as XIcon, Keyboard, Send, Volume2, VolumeX, Minus } from "lucide-react";
import axios from "axios";
import { JarvisChart } from "./JarvisChart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- iOS Safari audio compression ----
// iOS Safari ignores MediaRecorder's audioBitsPerSecond hint and records at
// ~10 Mbps AAC, producing 10-18 MB files for short recordings — which time
// out on cellular uploads. We decode + downsample to 16 kHz mono PCM WAV
// (~32 KB/sec) before upload, keeping voice intelligibility for Whisper
// while making the file uploadable on any network.

function audioBufferToWav16kMono(audioBuffer) {
  const numSamples = audioBuffer.length;
  const sampleRate = audioBuffer.sampleRate;
  const buffer = new ArrayBuffer(44 + numSamples * 2);
  const view = new DataView(buffer);
  // RIFF header
  view.setUint32(0, 0x46464952, true); // "RIFF"
  view.setUint32(4, 36 + numSamples * 2, true);
  view.setUint32(8, 0x45564157, true); // "WAVE"
  // fmt subchunk
  view.setUint32(12, 0x20746d66, true); // "fmt "
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  // data subchunk
  view.setUint32(36, 0x61746164, true); // "data"
  view.setUint32(40, numSamples * 2, true);
  const channel = audioBuffer.getChannelData(0);
  for (let i = 0; i < numSamples; i++) {
    const s = Math.max(-1, Math.min(1, channel[i]));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
  }
  return new Blob([buffer], { type: "audio/wav" });
}

async function compressVoiceBlob(blob) {
  // Returns the original blob unchanged if anything goes wrong — better to
  // upload a big file than fail outright.
  try {
    const arrayBuf = await blob.arrayBuffer();
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return blob;
    const ctx = new Ctx();
    const decoded = await new Promise((resolve, reject) => {
      // decodeAudioData has both promise-based and callback-based forms;
      // Safari requires the callback form on older versions.
      const p = ctx.decodeAudioData(arrayBuf, resolve, reject);
      if (p && typeof p.then === "function") p.then(resolve, reject);
    });
    const targetRate = 16000;
    const targetLength = Math.max(1, Math.ceil(decoded.duration * targetRate));
    const Offline = window.OfflineAudioContext || window.webkitOfflineAudioContext;
    if (!Offline) {
      try { ctx.close(); } catch (_) { /* noop */ }
      return blob;
    }
    const offline = new Offline(1, targetLength, targetRate);
    const src = offline.createBufferSource();
    src.buffer = decoded;
    src.connect(offline.destination);
    src.start(0);
    const rendered = await offline.startRendering();
    try { ctx.close(); } catch (_) { /* noop */ }
    return audioBufferToWav16kMono(rendered);
  } catch (e) {
    console.warn("Voice compression failed, uploading original blob", e);
    return blob;
  }
}

/**
 * JARVIS — voice + typed assistant overlay, Iron Man HUD aesthetic.
 *
 * Visual language:
 *   • Deep space-navy background (#040810) with cyan luminous accents
 *     (#00E5FF) — directly inspired by the Mark IV arc-reactor HUD.
 *   • Central reactor orb at the top of the panel; concentric rings ripple
 *     outward whose tempo varies with status (idle breathe, listening
 *     rapid pulse, speaking radiating waves, processing rotational scan).
 *   • Holographic text: JetBrains Mono with a faint cyan glow.
 *
 * Two entry modes:
 *   • Voice mode (mic launcher): greets aloud, then listens continuously
 *     until closed.
 *   • Text mode (keyboard launcher): opens silently, user types commands.
 *     Bypasses the iOS Safari mic block entirely.
 *
 * Backend contract: /api/jarvis/intent returns
 *   { actions: [{action, params}, ...], spoken: "..." }
 * Each action is dispatched to the host's onAction handler in order.
 */

// =============================================================================
// JARVIS Hologram — Iron Man HUD-style animated SVG
// =============================================================================
// Stacked rotating SVG layers create the canonical Tony Stark JARVIS look:
//   • Outer tick-marked ring with rotating angle labels
//   • Mid ring with broken bracket arcs
//   • Diagonal gyroscope ring (tilted ellipse)
//   • Crosshair + inner circles
//   • Arc-reactor core with breathing glow
//   • Corner data readouts that flicker with random-looking tech values
// All strokes glow cyan via SVG <filter> drop-shadow. Animation entirely
// CSS-driven via the .jarvis-holo-* classes below. Hooks into the existing
// orbState (.jarvis-orb-listen/-speak/-process/-error) to react to voice.
// JARVIS Plexus Orb — Canvas-rendered 3D particle-network sphere
// (matching the TikTok reference the user provided). ~140 particles
// distributed on a sphere via the Fibonacci-sphere algorithm, projected
// to 2D each frame with simple perspective, with glowing cyan lines
// drawn between any pair within a distance threshold. Particles jitter
// slightly each frame so the connecting lines shimmer (the signature
// plexus "alive" look). Voice level via --jarvis-level (read per-frame
// from the wrapper element) drives:
//   • per-particle jitter amplitude (so the mesh visibly "shakes" with
//     the voice),
//   • line opacity boost (mesh brightens during voice activity),
//   • overall sphere scale (gentle swell).
// All math is plain JS — no three.js needed.
const JarvisHologram = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    // Resolve the wrapper element so we can read --jarvis-level + state
    // class (.jarvis-orb-listen / -speak / -process / -error) per frame.
    const wrap = canvas.closest(".jarvis-voice-reactive") || canvas.parentElement;
    // Fibonacci-sphere — uniformly distributes N points on a unit sphere.
    // Each particle stores its CANONICAL (jitter-free) position plus a
    // per-particle phase offset for organic jitter.
    const N = 140;
    const particles = [];
    const golden = Math.PI * (3 - Math.sqrt(5));
    for (let i = 0; i < N; i++) {
      const y = 1 - (i / (N - 1)) * 2;          // -1..1
      const r = Math.sqrt(1 - y * y);
      const theta = golden * i;
      // Each particle gets:
      //  • a canonical position (ox/oy/oz) on the unit sphere
      //  • multiple independent oscillator phases & speeds for noisy drift
      //  • a wave-axis (which axis of the sphere it lives most on) so
      //    traveling waves don't all peak at the same time
      particles.push({
        ox: Math.cos(theta) * r,
        oy: y,
        oz: Math.sin(theta) * r,
        // Two independent oscillators per axis → quasi-Perlin wander.
        phx1: Math.random() * Math.PI * 2,
        phx2: Math.random() * Math.PI * 2,
        phy1: Math.random() * Math.PI * 2,
        phy2: Math.random() * Math.PI * 2,
        phz1: Math.random() * Math.PI * 2,
        phz2: Math.random() * Math.PI * 2,
        sp1: 0.4 + Math.random() * 0.6,
        sp2: 1.1 + Math.random() * 1.1,
        // Radial pulse phase — staggered so the surface ripples instead
        // of expanding uniformly. Particles near the same longitude
        // pulse together → traveling-wave effect.
        wavePhase: Math.atan2(Math.sin(theta) * r, Math.cos(theta) * r) + y * 1.7,
        // Random brightness multiplier — sells "individual life".
        glow: 0.7 + Math.random() * 0.6,
      });
    }
    let rotY = 0, rotX = 0;
    let rafId;
    let dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    // Voice-decay envelope — when level spikes we capture the peak and
    // let it bleed off over ~0.4s. This makes the orb "ring" after a
    // loud syllable instead of instantly settling.
    let voiceEnv = 0;
    // Resize canvas to its rendered CSS size (×DPR for crisp lines).
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    // Read the state class once per frame — much cheaper than per-particle.
    const stateColor = () => {
      const cls = wrap ? wrap.className : "";
      if (cls.includes("jarvis-orb-listen")) return [255, 110, 110];
      if (cls.includes("jarvis-orb-error"))  return [255, 149, 64];
      return [0, 229, 255]; // default cyan (speak, process, idle)
    };
    const t0 = performance.now();
    let lastT = t0;
    const draw = (now) => {
      const W = canvas.width, H = canvas.height;
      const t = (now - t0) / 1000;
      const dt = Math.min(0.05, (now - lastT) / 1000); // clamp on tab-resume
      lastT = now;
      // Voice level: read from CSS var (60fps RAF pump already writes it).
      const level = wrap
        ? parseFloat(getComputedStyle(wrap).getPropertyValue("--jarvis-level")) || 0
        : 0;
      // Envelope: fast attack, slow decay so the orb visibly "rings"
      // after every loud syllable instead of resetting instantly.
      if (level > voiceEnv) voiceEnv = level;                  // attack: snap to peak
      else voiceEnv = Math.max(0, voiceEnv - dt * 1.8);        // decay: ~0.55s to silent
      const v = Math.min(1, voiceEnv);
      const [R, G, B] = stateColor();
      // FULLY transparent clear — the canvas blends naturally with the
      // panel/page background behind it.
      ctx.clearRect(0, 0, W, H);
      // Add a SLOW pulse to the sphere's effective radius so even with
      // no voice the orb breathes in/out (~6% amplitude, 4s period).
      // During voice, +14% on top.
      const breathePulse = Math.sin(t * (Math.PI * 2 / 4)) * 0.06;
      const effectiveRadiusBoost = breathePulse + v * 0.14;
      // Sphere physical sizing — derived from canvas dim so the mesh
      // fills the wrapper regardless of container size.
      const cx = W / 2, cy = H / 2;
      const radius = Math.min(W, H) * 0.42 * (1 + effectiveRadiusBoost);
      const focal = radius * 2.2;
      // Rotation — visibly moving even at idle, dramatically tumbling
      // during speech. Voice envelope (v) makes the swish *persist*
      // through the tail of every syllable instead of stopping abruptly.
      rotY += 0.012 + v * 0.055;
      rotX += 0.006 + v * 0.034;
      // Z-axis wobble — runs at idle (slow), explodes with voice.
      const rotZ = Math.sin(t * 0.9) * 0.06 + Math.sin(t * 2.6) * 0.18 * v;
      const sY = Math.sin(rotY), cY = Math.cos(rotY);
      const sX = Math.sin(rotX), cX = Math.cos(rotX);
      const sZ = Math.sin(rotZ), cZ = Math.cos(rotZ);
      // Project all particles to screen-space once per frame.
      const proj = new Array(N);
      // Continuous DRIFT amplitude — every particle wanders around its
      // canonical position via two stacked sine oscillators per axis
      // (quasi-Perlin). Idle baseline 0.045 (visible wandering), voice
      // peak ≈ 0.30 (mesh visibly churns).
      const driftAmp = 0.045 + v * 0.26;
      // Traveling-wave amplitude — a radial pulse sweeps across the
      // sphere (peak amplitude tied to voice), so the surface looks
      // like it's RIPPLING not just inflating.
      const waveAmp = 0.05 + v * 0.32;
      const waveSpeed = 1.4 + v * 4.0; // faster ripples when loud
      // Radial puff — a sharp outward push that fires on every voice
      // peak and bleeds off with the envelope. Sells the "alive"
      // reaction the user described.
      const puff = v * 0.18;
      for (let i = 0; i < N; i++) {
        const p = particles[i];
        // Per-axis wander (two-oscillator noise approximation).
        const dx = (Math.sin(t * p.sp1 + p.phx1) + Math.sin(t * p.sp2 + p.phx2) * 0.6) * driftAmp;
        const dy = (Math.sin(t * p.sp1 + p.phy1) + Math.sin(t * p.sp2 + p.phy2) * 0.6) * driftAmp;
        const dz = (Math.sin(t * p.sp1 + p.phz1) + Math.sin(t * p.sp2 + p.phz2) * 0.6) * driftAmp;
        // Traveling wave — depends on the particle's wavePhase so the
        // ripple visibly TRAVELS around the surface instead of all
        // particles peaking at once.
        const wave = Math.sin(t * waveSpeed + p.wavePhase * 2.3) * waveAmp;
        const rScale = 1 + wave + puff;
        let x = (p.ox + dx) * rScale;
        let y = (p.oy + dy) * rScale;
        let z = (p.oz + dz) * rScale;
        // Rotate around Y axis
        let x1 = x * cY + z * sY;
        let z1 = -x * sY + z * cY;
        // Rotate around X axis
        let y2 = y * cX - z1 * sX;
        let z2 = y * sX + z1 * cX;
        // Z-axis roll (voice-driven)
        const x3 = x1 * cZ - y2 * sZ;
        const y3 = x1 * sZ + y2 * cZ;
        // Perspective project
        const k = focal / (focal - z2 * radius);
        const sxp = cx + x3 * radius * k;
        const syp = cy + y3 * radius * k;
        proj[i] = { x: sxp, y: syp, z: z2, k, glow: p.glow };
      }
      // Draw connecting lines (the "plexus" mesh).
      const LINK_DIST = 0.55;
      const lineAlpha = 0.18 + v * 0.62;
      ctx.lineWidth = 0.7 * dpr;
      for (let i = 0; i < N; i++) {
        const a = proj[i];
        const pa = particles[i];
        for (let jj = i + 1; jj < N; jj++) {
          const pb = particles[jj];
          const dxL = pa.ox - pb.ox, dyL = pa.oy - pb.oy, dzL = pa.oz - pb.oz;
          const d2 = dxL * dxL + dyL * dyL + dzL * dzL;
          if (d2 > LINK_DIST * LINK_DIST) continue;
          const b = proj[jj];
          const fade = 1 - Math.sqrt(d2) / LINK_DIST;
          // Depth fade — back of sphere is dimmer so the silhouette reads.
          const depth = (a.z + b.z) * 0.5;
          const depthFade = 0.4 + Math.max(0, 0.6 - depth * 0.4);
          const alpha = lineAlpha * fade * depthFade;
          ctx.strokeStyle = `rgba(${R}, ${G}, ${B}, ${alpha.toFixed(3)})`;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }
      // Draw particles as glowing dots on top of the lines. Closer dots
      // are larger and brighter. Each particle has its own random `glow`
      // factor so they twinkle individually instead of moving in lock-step.
      for (let i = 0; i < N; i++) {
        const a = proj[i];
        const baseSz = (1.4 + a.k * 1.1) * dpr;
        const sz = baseSz * (1 + v * 0.45) * a.glow;
        const aBright = (0.55 + Math.max(0, 0.5 - a.z * 0.5)) * a.glow;
        // Soft halo behind each dot — sells "alive" without being heavy.
        if (v > 0.05) {
          const haloA = Math.min(0.35, aBright * v * 0.6);
          ctx.fillStyle = `rgba(${R}, ${G}, ${B}, ${haloA.toFixed(3)})`;
          ctx.beginPath();
          ctx.arc(a.x, a.y, sz * 2.4, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.fillStyle = `rgba(${R}, ${G}, ${B}, ${Math.min(1, aBright).toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(a.x, a.y, sz, 0, Math.PI * 2);
        ctx.fill();
      }
      rafId = requestAnimationFrame(draw);
    };
    rafId = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, []);
  return (
    <div className="jarvis-orb-stage" aria-hidden="true">
      <canvas ref={canvasRef} className="jarvis-plexus-canvas" />
    </div>
  );
};

export function JarvisAssistant({ getContext, onAction, onBeforeIntent, autoOpenSignal }) {
  const [isOpen, setIsOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [mode, setMode] = useState("voice"); // 'voice' | 'text'
  const [status, setStatus] = useState("idle"); // idle|greeting|listening|processing|speaking|ready|error
  const [transcript, setTranscript] = useState("");
  const [reply, setReply] = useState("");
  const [error, setError] = useState("");
  const [typed, setTyped] = useState("");
  const [ttsMuted, setTtsMuted] = useState(false);
  // Inline chart shown in place of the hologram between turns. Set by
  // `show_chart` actions returned from onAction; cleared at the start of
  // every new user turn so the hologram returns while JARVIS thinks/speaks.
  const [chartView, setChartView] = useState(null);
  // Short conversation memory — used so follow-ups like "show me" can be
  // resolved against the previous turn. Capped to the most recent ~6 entries.
  const [history, setHistory] = useState([]);

  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const audioElRef = useRef(null);
  const cancelledRef = useRef(false);
  const streamRef = useRef(null);
  const modeRef = useRef("voice");
  const ttsMutedRef = useRef(false);
  // Set true when the current turn's reply is a verbal farewell — the
  // continuous-listen loop checks this on audio.ended and stops itself
  // instead of restarting the mic, so JARVIS closes cleanly after his
  // own "Goodbye, sir." plays.
  const dismissAfterReplyRef = useRef(false);
  // Counts consecutive transcribe failures so a broken Whisper / network
  // doesn't trap JARVIS in an "apologies, sir" loop.
  const transcribeFailCountRef = useRef(0);
  // Session ID — increments every time the user taps the launcher.
  // Any async operation (queued dismiss closeJarvis, listen-loop kick,
  // pending speak) captures the session ID at start and bails if a new
  // session has begun. Eliminates the race where the user re-opens
  // JARVIS during the 100 ms post-farewell window and the queued close
  // kills the fresh session.
  const sessionIdRef = useRef(0);
  // Detect if we're running inside an iframe (e.g. the Emergent preview
  // panel). On iOS Safari, mic permission is blocked in iframes — we use
  // this to surface a clear "open in Safari" CTA.
  const inIframe = typeof window !== "undefined" && window.self !== window.top;
  const directUrl = typeof window !== "undefined" ? window.location.href : "";

  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { ttsMutedRef.current = ttsMuted; }, [ttsMuted]);

  // Persistent playback context — created/unlocked inside the user gesture
  // (openVoice) so iOS allows audio playback for the rest of the session.
  // We also hook an AnalyserNode in here so the hologram can react to the
  // amplitude of JARVIS's voice in real time.
  const playbackCtxRef = useRef(null);
  const currentSourceRef = useRef(null);
  // AnalyserNode + RAF id for the voice-reactive hologram pulse.
  const voiceAnalyserRef = useRef(null);
  const voiceRafRef = useRef(0);
  // Element whose CSS variable we update each frame. The hologram CSS
  // reads `--jarvis-level` (0–1) to scale + glow with the voice.
  const orbWrapperRef = useRef(null);

  // -------------------------------------------------------------------------
  // 🔒 LOCKED — iOS Safari / PWA audio playback
  // -------------------------------------------------------------------------
  // Web Audio API (decodeAudioData + BufferSource) is unreliable on iOS:
  // the AudioContext gets silently re-suspended between JARVIS turns when
  // the app loses focus or the screen sleeps, and calling ctx.resume()
  // from an ASYNC callback (after the fetch resolves) does NOT satisfy
  // iOS's user-gesture requirement. Result: voice plays once and then
  // intermittently goes silent for the rest of the session.
  //
  // The bulletproof fix is to use a persistent HTMLAudioElement. Once
  // unlocked by ANY single user gesture (the initial tap that opens
  // JARVIS), the element stays playable indefinitely — the OS treats
  // subsequent .play() calls as if they were part of the original gesture.
  //
  // We keep the AnalyserNode visualizer DISABLED here because
  // createMediaElementSource permanently routes the audio through the
  // Web Audio graph (so suspended context = silent). The orb still
  // animates via a synthetic time-based pulse driven from currentTime,
  // which looks alive without coupling to the WebAudio state machine.
  // -------------------------------------------------------------------------
  const playAudioBlob = useCallback(async (blob) => {
    let audio = audioElRef.current;
    if (!audio) {
      audio = new Audio();
      audio.preload = "auto";
      audio.crossOrigin = "anonymous";
      // iOS REQUIRES `playsInline` to allow in-page playback. Without it
      // Safari can hijack the audio with the native player UI.
      audio.setAttribute("playsinline", "true");
      audioElRef.current = audio;
    }
    // Make sure the blob has a usable MIME type. FastAPI's StreamingResponse
    // delivers `audio/mpeg`; if for some reason the type is empty Safari
    // will refuse to play. Reconstruct as an explicit MP3 blob.
    const playable = blob.type && blob.type.startsWith("audio/")
      ? blob
      : new Blob([await blob.arrayBuffer()], { type: "audio/mpeg" });
    const url = URL.createObjectURL(playable);
    audio.src = url;
    // Calling .load() forces Safari to commit to the new src before .play()
    // — without this, iOS sometimes plays the PREVIOUS src silently.
    try { audio.load(); } catch (_) { /* noop */ }

    // Drive the orb pulse from a synthetic envelope while audio plays.
    // This replaces the AnalyserNode-based amplitude readout. We start
    // an interval that mimics speech-energy with a slow LFO modulated
    // by a faster jitter — the orb visibly "talks" without depending
    // on Web Audio.
    if (orbWrapperRef.current) {
      orbWrapperRef.current.style.setProperty("--jarvis-level", "0");
    }
    const startTs = performance.now();
    const stopPulse = () => {
      cancelAnimationFrame(voiceRafRef.current);
      if (orbWrapperRef.current) {
        orbWrapperRef.current.style.setProperty("--jarvis-level", "0");
      }
    };
    const pump = () => {
      // Stop when audio ends (event handler also fires `stopPulse`).
      if (audio.paused || audio.ended) {
        stopPulse();
        return;
      }
      const t = (performance.now() - startTs) / 1000;
      // Speech-like envelope: slow rolling cadence + faster jitter +
      // random spikes so the orb feels alive instead of metronomic.
      const cadence = (Math.sin(t * 5.1) + 1) * 0.32;
      const jitter = (Math.sin(t * 17.3) + Math.sin(t * 23.7)) * 0.07;
      const level = Math.max(0, Math.min(1, cadence + jitter));
      if (orbWrapperRef.current) {
        orbWrapperRef.current.style.setProperty("--jarvis-level", level.toFixed(3));
      }
      voiceRafRef.current = requestAnimationFrame(pump);
    };
    voiceRafRef.current = requestAnimationFrame(pump);

    return new Promise((resolve, reject) => {
      const cleanup = () => {
        audio.removeEventListener("ended", onEnded);
        audio.removeEventListener("error", onError);
        try { URL.revokeObjectURL(url); } catch (_) { /* noop */ }
        stopPulse();
      };
      const onEnded = () => { cleanup(); resolve(); };
      const onError = () => {
        const code = audio.error ? audio.error.code : "?";
        cleanup();
        // MEDIA_ERR_ABORTED = 1 — fired when src changes mid-play.
        // This is the EXPECTED outcome when the user interrupts JARVIS
        // mid-sentence (their next tap changes src to the new reply).
        // Don't surface this as a user-visible error.
        if (code === 1 || code === 20) {
          resolve();
        } else {
          reject(new Error(`HTMLAudioElement playback error code=${code}`));
        }
      };
      audio.addEventListener("ended", onEnded);
      audio.addEventListener("error", onError);
      // .play() returns a Promise on modern browsers — chain it so any
      // autoplay/policy rejection surfaces as a real error (caught by
      // speak()'s catch block and rendered as a visible chip).
      const p = audio.play();
      if (p && typeof p.catch === "function") {
        p.catch((e) => {
          cleanup();
          // AbortError on play() is the EXPECTED outcome of the user
          // interrupting (next tap changes src → previous play aborts).
          // Treat as a no-op so we don't show a scary red chip.
          if (e?.name === "AbortError") {
            resolve();
          } else {
            reject(e);
          }
        });
      }
    });
  }, []);

  // Prime the Web Audio playback context inside a user gesture so iOS
  // Safari unlocks audio for the rest of the session. A silent 1-sample
  // buffer is enough — what iOS actually wants is to see an AudioContext
  // do something inside a tap event.
  // Tracks whether the HTMLAudioElement has been successfully primed
  // by ANY successful play() inside a user gesture. We track this
  // separately from `unlockAudioRef` (which only covers Web Audio
  // context unlock) because the two can fail independently on iOS.
  const audioElPrimedRef = useRef(false);
  const unlockAudioRef = useRef(false);
  const unlockAudio = useCallback(() => {
    // Web Audio context unlock — covers the orb visualizer side.
    if (!unlockAudioRef.current) {
      try {
        const Ctor = window.AudioContext || window.webkitAudioContext;
        if (Ctor && !playbackCtxRef.current) {
          playbackCtxRef.current = new Ctor();
        }
        const ctx = playbackCtxRef.current;
        if (ctx) {
          if (ctx.state === "suspended") {
            ctx.resume().catch(() => { /* noop */ });
          }
          const buf = ctx.createBuffer(1, 1, 22050);
          const src = ctx.createBufferSource();
          src.buffer = buf;
          src.connect(ctx.destination);
          src.start(0);
          unlockAudioRef.current = true;
        }
      } catch (_) { /* noop */ }
    }
    // HTMLAudioElement unlock — this is the CRITICAL one for iOS PWA.
    // We try every call until one succeeds (audioElPrimedRef.current
    // gates re-attempts). The previous version used a 256-byte MP3
    // data URI that iOS sometimes rejects with NotSupportedError →
    // unlock never succeeded → all subsequent plays got NotAllowedError.
    //
    // The pattern that actually works on iOS 16/17/18:
    //   1. Use a tiny VALID WAV blob (not MP3 data URI — Safari is
    //      pickier about MP3 codec headers).
    //   2. Set `muted = true` first — muted playback is virtually
    //      never blocked by autoplay policy.
    //   3. Call play() inside the user gesture. The CALL itself is
    //      what iOS uses to bless the element — whether the promise
    //      resolves OR rejects (because we pause too soon) doesn't
    //      matter. Mark the element primed SYNCHRONOUSLY so subsequent
    //      taps NEVER touch audio.src again (touching src after a
    //      real blob is loaded would abort the JARVIS reply mid-play
    //      with AbortError).
    if (!audioElPrimedRef.current) {
      try {
        let audio = audioElRef.current;
        if (!audio) {
          audio = new Audio();
          audio.preload = "auto";
          audio.setAttribute("playsinline", "true");
          audioElRef.current = audio;
        }
        // Tiny valid WAV: 1 frame of silence, 8-bit mono, 8000 Hz.
        audio.src = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";
        audio.muted = true;
        audio.volume = 0;
        const p = audio.play();
        // Commit prime status IMMEDIATELY — the play() CALL inside the
        // gesture is what unlocks the element; the promise outcome is
        // irrelevant. This prevents re-running the unlock on every
        // future tap, which would re-set audio.src and abort the
        // current playback with AbortError.
        audioElPrimedRef.current = true;
        const finishPrime = () => {
          try {
            audio.pause();
            audio.currentTime = 0;
            audio.muted = false;
            audio.volume = 1.0;
          } catch (_) { /* noop */ }
        };
        if (p && typeof p.then === "function") {
          p.then(finishPrime).catch((err) => {
            // Even if play() rejects (often AbortError when we pause
            // too fast), the element is primed — keep that flag set.
            finishPrime();
            console.warn("JARVIS audio prime play() rejected (still primed):", err?.name, err?.message);
          });
        } else {
          finishPrime();
        }
      } catch (_) { /* noop */ }
    }
    // Web Speech API warm-up (final fallback).
    try {
      const synth = typeof window !== "undefined" ? window.speechSynthesis : null;
      if (synth && typeof window.SpeechSynthesisUtterance === "function") {
        const u = new window.SpeechSynthesisUtterance(" ");
        u.volume = 0;
        synth.speak(u);
      }
    } catch (_) { /* noop */ }
  }, []);

  // Web Speech API fallback — used when ElevenLabs MP3 playback fails (iOS
  // PWA autoplay restrictions, network hiccup, etc.). The voice isn't as
  // characterful as ElevenLabs but JARVIS will at least speak.
  const speakWebFallback = useCallback((text) => {
    return new Promise((resolve) => {
      try {
        const synth = typeof window !== "undefined" ? window.speechSynthesis : null;
        if (!synth || typeof window.SpeechSynthesisUtterance !== "function") {
          resolve();
          return;
        }
        synth.cancel();
        const u = new window.SpeechSynthesisUtterance(text);
        u.rate = 1.0;
        u.pitch = 1.0;
        // Prefer a British male voice if the device has one — closest to JARVIS.
        const voices = synth.getVoices() || [];
        const preferred =
          voices.find((v) => /en-GB/i.test(v.lang) && /male/i.test(v.name)) ||
          voices.find((v) => /en-GB/i.test(v.lang)) ||
          voices.find((v) => /Daniel/i.test(v.name)) ||
          voices.find((v) => /en-US/i.test(v.lang)) ||
          null;
        if (preferred) u.voice = preferred;
        u.onend = () => resolve();
        u.onerror = () => resolve();
        synth.speak(u);
      } catch (_) {
        resolve();
      }
    });
  }, []);

  // Speak text via the backend TTS endpoint. Falls back to the browser's
  // Web Speech API when MP3 playback fails (iOS autoplay restriction, etc.)
  // so JARVIS never goes silent on the user.
  const speak = useCallback(async (text) => {
    if (!text) return;
    // Capture session at start — if a tap interrupts us during the
    // /jarvis/speak fetch or during playback, the bumped sessionIdRef
    // makes us bail BEFORE the stale TTS audio reaches the speakers.
    const speakSession = sessionIdRef.current;
    setReply(text);
    if (ttsMutedRef.current) {
      setStatus("ready");
      return;
    }
    setStatus("speaking");
    try {
      const res = await axios.post(
        `${API}/jarvis/speak`,
        { text },
        { responseType: "blob", timeout: 30000 }
      );
      if (cancelledRef.current) return;
      if (sessionIdRef.current !== speakSession) return; // interrupted
      await playAudioBlob(res.data);
    } catch (e) {
      // Surface the REAL reason JARVIS went silent so the user sees it
      // directly in the panel without needing DevTools. Only fires on
      // genuine playback failure — the Web Speech fallback below still
      // runs so JARVIS never goes mute.
      let reason = "";
      if (e?.response) {
        const status = e.response.status;
        let detail = "";
        try {
          if (e.response.data instanceof Blob) {
            detail = (await e.response.data.text()).slice(0, 200);
          } else if (typeof e.response.data === "string") {
            detail = e.response.data.slice(0, 200);
          } else if (e.response.data?.detail) {
            detail = String(e.response.data.detail).slice(0, 200);
          }
        } catch (_) { /* noop */ }
        reason = `Speak endpoint ${status}: ${detail || "no detail"}`;
      } else {
        reason = e?.message || "Voice playback failed";
      }
      console.warn("JARVIS voice failed:", reason, e);
      setError(`🔇 ${reason}`);
      if (sessionIdRef.current !== speakSession) return; // interrupted
      // Fallback so the user always hears the reply via OS TTS.
      try {
        if (!cancelledRef.current) await speakWebFallback(text);
      } catch (_) { /* noop */ }
      // The audio.ended event won't fire for the fallback path, so kick
      // the listen loop forward manually (voice mode only).
      if (sessionIdRef.current !== speakSession) return; // interrupted
      setStatus("ready");
      if (modeRef.current === "voice" && !cancelledRef.current && !dismissAfterReplyRef.current) {
        setTimeout(() => {
          if (!cancelledRef.current && sessionIdRef.current === speakSession) {
            runVoiceTurnRef.current?.();
          }
        }, 250);
      }
    }
  }, [playAudioBlob, speakWebFallback]);

  // Ref so speak() (defined before runVoiceTurn) can still kick the listen
  // loop on a TTS-fallback path without a circular dep.
  const runVoiceTurnRef = useRef(null);

  // Record from the mic with simple silence-detection VAD.
  const recordCommand = useCallback(async () => {
    setStatus("listening");
    setTranscript("");
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/mp4")
          ? "audio/mp4"
          : "";
      // Force a low bitrate (24 kbps) so 10-15s of voice ≈ 30–45 KB instead
      // of 1-2 MB. Critical for cellular uploads where high-bitrate audio
      // was timing out the 30s axios deadline.
      const mrOpts = mime
        ? { mimeType: mime, audioBitsPerSecond: 24000 }
        : { audioBitsPerSecond: 24000 };
      let mr;
      try {
        mr = new MediaRecorder(stream, mrOpts);
      } catch (_) {
        // Some Safari builds reject the bitrate option — fall back to defaults.
        mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      }
      mediaRecorderRef.current = mr;
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      const stopped = new Promise((resolve) => { mr.onstop = resolve; });

      mr.start();

      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = ctx;
      // iOS Safari sometimes creates the context in "suspended" state
      // when it's instantiated outside a user gesture (we're past the
      // initial tap by the time recordCommand runs). Resume it
      // explicitly so the VAD analyser actually receives audio data —
      // otherwise sawSpeech stays false forever and JARVIS would loop
      // silently without sending anything to Whisper.
      if (ctx.state === "suspended") {
        try { await ctx.resume(); } catch (_) { /* noop */ }
      }
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyserRef.current = analyser;
      src.connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      const startTs = Date.now();
      let silentSince = null;
      let sawSpeech = false;
      let loudSinceTs = null;          // when the current loud run started
      let loudDurationTotalMs = 0;     // cumulative loud time so far
      // VAD tuning — favour speed but reject brief noise spikes that
      // would otherwise let Whisper hallucinate ("thank you", "patients
      // have French names" etc.) on ambient sound. We only mark
      // `sawSpeech` after MIN_SPEECH_MS of cumulative loud signal.
      const SPEECH_RMS = 0.035;       // ambient-noise tolerant
      // User complained JARVIS cuts off mid-sentence on natural pauses.
      // Bumped from 700 → 1300 ms so brief breaths and "uh" pauses
      // inside a longer command no longer end the recording prematurely.
      // 1300 ms is the sweet-spot empirically between "feels responsive"
      // and "doesn't cut me off"; pushing to 1500+ makes the orb feel
      // sluggish to react.
      const SILENCE_MS = 1300;
      const MIN_SPEECH_MS = 250;       // sustained-speech minimum
      // Max single-command recording. Bumped from 10 → 25 s so longer
      // commands like "show me ODB payment cycles for hepatitis from
      // January 2026 until June 2026" can complete without being
      // truncated.
      const MAX_RECORD_MS = 25000;
      // Brief noise spikes during a silence window (HVAC, keyboard,
      // distant voices) shouldn't reset the silence timer — otherwise
      // JARVIS never stops listening in real-world environments. Track
      // a small grace budget: if loud frames during silence exceed
      // SILENCE_NOISE_GRACE_MS within the silence window, then yes,
      // restart silence; otherwise allow brief spikes to pass through.
      // Bumped 120 → 220 ms in tandem with the silence bump so brief
      // stutters / "um"s during a thoughtful pause aren't treated as
      // resumed speech.
      const SILENCE_NOISE_GRACE_MS = 220;
      let noiseInSilenceMs = 0;
      const tick = () => {
        if (mr.state !== "recording") return;
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        // Mirror the TTS-reactive pulse: while listening, drive the same
        // --jarvis-level CSS variable from the user's mic amplitude so the
        // hologram visibly resonates with the speaker's voice too. Uses
        // the same [0..~0.5] → [0..1] mapping as playAudioBlob() for
        // visual continuity between listening and speaking states.
        if (orbWrapperRef.current) {
          const level = Math.min(1, rms * 2.2);
          orbWrapperRef.current.style.setProperty("--jarvis-level", level.toFixed(3));
        }
        const loud = rms > SPEECH_RMS;
        const now = Date.now();
        if (loud) {
          if (loudSinceTs == null) loudSinceTs = now;
          loudDurationTotalMs += 16; // ~one RAF tick @60fps
          if (!sawSpeech && loudDurationTotalMs >= MIN_SPEECH_MS) {
            sawSpeech = true;
          }
          // Inside a silence window? Count this loud frame against the
          // noise grace budget instead of immediately resetting.
          if (sawSpeech && silentSince != null) {
            noiseInSilenceMs += 16;
            if (noiseInSilenceMs > SILENCE_NOISE_GRACE_MS) {
              // Genuine speech again — restart silence detection.
              silentSince = null;
              noiseInSilenceMs = 0;
            }
          } else {
            silentSince = null;
            noiseInSilenceMs = 0;
          }
        } else {
          loudSinceTs = null;
          if (sawSpeech) {
            if (silentSince == null) {
              silentSince = now;
              noiseInSilenceMs = 0;
            } else if (now - silentSince > SILENCE_MS) {
              try { mr.stop(); } catch (_) { /* noop */ }
              return;
            }
          }
        }
        if (now - startTs > MAX_RECORD_MS) {
          try { mr.stop(); } catch (_) { /* noop */ }
          return;
        }
        silenceTimerRef.current = requestAnimationFrame(tick);
      };
      silenceTimerRef.current = requestAnimationFrame(tick);

      await stopped;
      cancelAnimationFrame(silenceTimerRef.current);
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      try { ctx.close(); } catch (_) { /* noop */ }
      // Decay mic-driven pulse so the orb settles before the speaking
      // state takes over (playAudioBlob will re-drive it from TTS).
      if (orbWrapperRef.current) {
        orbWrapperRef.current.style.setProperty("--jarvis-level", "0");
      }

      if (cancelledRef.current) return null;
      const blob = new Blob(chunks, { type: mime || "audio/webm" });
      // Return whether VAD detected actual speech — the caller uses this
      // to skip the Whisper round-trip on silent windows, which prevents
      // Whisper from hallucinating our prompt text back as user words
      // (it tends to echo "Patients have French names" when given silence).
      return { blob, sawSpeech };
    } catch (e) {
      console.error("Mic record failed", e);
      // Common cause on iOS: iframe contexts can't request the mic. Surface
      // a clear, actionable message — and the panel separately renders an
      // "Open in Safari" button when inIframe is true.
      if (inIframe) {
        setError(
          "Microphone is blocked inside this preview iframe. Tap “Open in Safari” below to enable voice."
        );
      } else {
        setError(
          "Microphone access denied. Allow mic permission in Safari Settings and tap ENGAGE again."
        );
      }
      setStatus("error");
      return null;
    }
  }, [inIframe]);

  // Shared pipeline: take a transcript, hit the intent endpoint, run the
  // host's action handler for every action returned, then speak the reply.
  const processTranscript = useCallback(async (text) => {
    // Capture sessionId at the start — interruptToListen bumps it, so
    // if the user taps to interrupt while we're awaiting the intent
    // endpoint or speak() playback, the comparison below short-circuits
    // and the stale reply is dropped instead of being spoken/acted on.
    const turnSession = sessionIdRef.current;
    setTranscript(text);
    if (!text || !text.trim()) {
      await speak("I didn't catch that, sir. Could you repeat?");
      return;
    }
    // Chart stays sticky across turns now — the user dismisses it
    // manually via the BACK button on the chart. (Removed the
    // setChartView(null) reset that previously fired here.)
    setStatus("processing");
    // Let the host reset any per-intent scratch state (e.g. refs that track
    // "this intent's date range").
    try { if (onBeforeIntent) onBeforeIntent(); } catch (_) { /* noop */ }
    try {
      const intentRes = await axios.post(
        `${API}/jarvis/intent`,
        {
          transcript: text,
          context: getContext ? getContext() : {},
          history,
        },
        { timeout: 30000 }
      );
      // If the user tapped to interrupt while we were awaiting the
      // intent reply, abandon this turn entirely — they're talking now.
      if (sessionIdRef.current !== turnSession) return;
      const intent = intentRes.data || {};
      // New shape: { actions: [{action, params}, ...], spoken }
      let actions = Array.isArray(intent.actions) ? intent.actions : null;
      if (!actions) {
        actions = [{ action: intent.action || "unsupported", params: intent.params || {} }];
      }
      let spoken = intent.spoken || "";
      // Verbal "undo / go back / scratch that" → replay the second-to-
      // last assistant turn's actions from history. We need at least two
      // prior assistant turns (history.length >= 4) for "undo" to have
      // somewhere to revert TO; otherwise we politely refuse.
      const shouldUndo = actions.some((it) => it && it.action === "undo");
      if (shouldUndo) {
        // Find the most recent assistant entry BEFORE the one we're
        // undoing — that's the state we want to restore. History at this
        // point still represents pre-this-turn, so the *last* assistant
        // entry IS the turn to undo, and the one before it is the target.
        const lastAsstIdx = (() => {
          for (let i = history.length - 1; i >= 0; i--) {
            if (history[i]?.role === "assistant") return i;
          }
          return -1;
        })();
        const targetAsstIdx = (() => {
          for (let i = lastAsstIdx - 1; i >= 0; i--) {
            if (history[i]?.role === "assistant") return i;
          }
          return -1;
        })();
        if (targetAsstIdx < 0) {
          // Nothing earlier to revert to.
          spoken = spoken && !/revert|step|back|undo/i.test(spoken)
            ? spoken
            : "Nothing earlier to revert to, sir.";
          await speak(spoken);
          // Drop the undo turn from history so it doesn't pollute future
          // coreference resolution.
          return;
        }
        // Replay the target turn's actions through the same onAction
        // pipeline. The dashboard handlers are idempotent — re-applying
        // set_view_mode / set_date_range / show_chart simply restores
        // the same state we had then.
        const replay = (history[targetAsstIdx].actions || []).filter(
          (it) => it && it.action && !["chat", "get_time", "get_location", "get_weather", "dismiss", "undo", "unsupported"].includes(it.action),
        );
        let restoredChart = null;
        if (onAction && replay.length) {
          const orderRank = (a) => {
            if (a === "set_view_mode") return 0;
            if (a === "set_date_range") return 1;
            return 2;
          };
          const sorted = [...replay].sort(
            (a, b) => orderRank(a?.action) - orderRank(b?.action),
          );
          for (const item of sorted) {
            try {
              const override = await onAction(item.action, item.params || {});
              if (override && typeof override === "object" && override.chart) {
                restoredChart = override.chart;
              }
            } catch (e) { console.warn("undo replay handler threw", e); }
          }
        }
        // Restore the chart visual if the prior turn had one; otherwise
        // clear so the hologram comes back.
        setChartView(restoredChart);
        // Trim history: remove the user+assistant pair we just undid so
        // the next undo walks further back, and don't record this undo
        // turn itself (otherwise the model would treat it as a fresh
        // turn).
        setHistory((prev) => {
          const sliced = [...prev];
          // Drop the last assistant entry and any trailing user entry.
          while (sliced.length && sliced[sliced.length - 1].role === "assistant") sliced.pop();
          while (sliced.length && sliced[sliced.length - 1].role === "user") sliced.pop();
          return sliced;
        });
        await speak(spoken || "Reverted, sir.");
        return;
      }
      // Verbal "exit / goodbye / that will be all" → close JARVIS after
      // the farewell finishes playing.
      const shouldDismiss = actions.some(
        (it) => it && it.action === "dismiss",
      );
      if (shouldDismiss) {
        dismissAfterReplyRef.current = true;
        if (!spoken) spoken = "Very well, sir. Standing down.";
        // Drop the dismiss action from the regular onAction loop — the
        // dashboard has no handler for it, and we close from here.
        actions = actions.filter((it) => it && it.action !== "dismiss");
      }
      // Pre-scan actions to extract any date range / view mode set in
      // earlier steps. These get injected as defaults into later
      // report_metric calls whose params don't already carry them, so the
      // metric is computed against the user's requested window rather
      // than the stale visible-range fallback (React setState hasn't
      // propagated within this single async pass).
      let chainedRange = null; // { start, end }
      let chainedView = null;
      for (const it of actions) {
        if (it && it.action === "set_date_range" && it.params?.start && it.params?.end) {
          chainedRange = { start: it.params.start, end: it.params.end };
        }
        if (it && it.action === "set_view_mode" && it.params?.mode) {
          const m = String(it.params.mode).toLowerCase().replace(/_/g, "-");
          if (m === "hcv" || m === "non-hcv") chainedView = m;
        }
      }
      // Track whether this turn included a navigation action so we can
      // auto-minimize the panel afterwards (so the user can actually see
      // the data the dashboard just scrolled/expanded to).
      let didNavigate = false;
      // Captures any chart spec returned by an onAction handler (e.g. the
      // dashboard's show_chart implementation). Rendered after the spoken
      // summary finishes.
      let chartFromAction = null;
      const NAV_ACTIONS = new Set([
        "scroll_to",
        "open_consultant_drill",
        "show_patient",
        "show_birthday_month",
        "set_view_mode",
        "set_date_range",
        "report_metric",
      ]);
      if (onAction) {
        // Reorder so set_view_mode runs BEFORE set_date_range. The dashboard
        // has a viewMode-change useEffect that wipes customRange to null —
        // applying view-mode first lets that reset happen, then the date
        // range applies cleanly on top.
        const orderRank = (a) => {
          if (a === "set_view_mode") return 0;
          if (a === "set_date_range") return 1;
          return 2;
        };
        const sortedActions = [...actions].sort(
          (a, b) => orderRank(a?.action) - orderRank(b?.action)
        );
        for (const item of sortedActions) {
          const a = item && item.action;
          let p = (item && item.params) || {};
          if (!a) continue;
          // Inject chained context into report_metric if missing.
          if (a === "report_metric") {
            const next = { ...p };
            if (chainedRange) {
              if (!next.start) next.start = chainedRange.start;
              if (!next.end) next.end = chainedRange.end;
            }
            if (chainedView && !next.view_mode) {
              next.view_mode = chainedView === "hcv" ? "hcv" : "non_hcv";
            }
            p = next;
          }
          if (NAV_ACTIONS.has(a)) didNavigate = true;
          try {
            const override = await onAction(a, p);
            if (typeof override === "string" && override.trim()) {
              spoken = override;
            } else if (override && typeof override === "object") {
              if (typeof override.spoken === "string" && override.spoken.trim()) {
                spoken = override.spoken;
              }
              if (override.chart && typeof override.chart === "object") {
                chartFromAction = override.chart;
              }
            }
          } catch (e) {
            console.warn("onAction handler threw", e);
          }
        }
      }
      // Update conversation history (cap to last 6 entries).
      // We include a compact summary of the action(s) the assistant
      // executed so the backend LLM can resolve follow-ups like "show
      // me that again", "the same for HCV", "what about for April
      // instead" — i.e. re-issue the prior action with modified params.
      const actionSummary = actions
        .map((it) => {
          if (!it || !it.action) return null;
          const params = it.params || {};
          const compact = {};
          for (const key of [
            "chart", "mode", "view_mode", "start", "end", "limit",
            "metric", "drug", "patient", "month", "section", "type",
          ]) {
            if (params[key] !== undefined && params[key] !== null && params[key] !== "") {
              compact[key] = params[key];
            }
          }
          return { action: it.action, params: compact };
        })
        .filter(Boolean);
      setHistory((prev) => {
        const next = [
          ...prev,
          { role: "user", text },
          {
            role: "assistant",
            text: spoken || "Done, sir.",
            actions: actionSummary,
          },
        ];
        return next.slice(-6);
      });
      // Stash the chart so it appears once JARVIS finishes speaking
      // (the render gate hides it while status === 'speaking').
      if (chartFromAction) setChartView(chartFromAction);
      await speak(spoken || "Done, sir.");
      // If the user tapped to interrupt while JARVIS was speaking,
      // abandon all post-speak logic (dismiss timer, auto-minimize).
      if (sessionIdRef.current !== turnSession) return;
      // Verbal dismiss: close the panel once the farewell finishes.
      // closeJarvis flips cancelledRef so the continuous-listen loop
      // (which also fires on the same audio.ended event) bails cleanly.
      if (shouldDismiss) {
        const dismissSession = sessionIdRef.current;
        // Small pause so the audio element fully releases before unmount.
        setTimeout(() => {
          // Bail if the user has already tapped the launcher again —
          // openVoice incremented sessionIdRef so the IDs no longer match.
          // Prevents the queued close from killing a fresh re-open.
          if (sessionIdRef.current !== dismissSession) return;
          if (audioElRef.current) {
            try { audioElRef.current.pause(); } catch (_) { /* noop */ }
          }
          closeJarvis();
        }, 100);
        return;
      }
      // After speaking, if we navigated the dashboard, shrink the panel so
      // the user can actually interact with what JARVIS just opened.
      if (didNavigate) {
        // Brief pause so the spoken reply finishes before we collapse.
        setTimeout(() => {
          if (!cancelledRef.current) setMinimized(true);
        }, 350);
      }
    } catch (e) {
      console.error("JARVIS intent failed", e);
      const raw = e?.response?.data?.detail || e?.message || "";
      const isBudget = /budget/i.test(raw) && /exceed/i.test(raw);
      if (isBudget) {
        setError(
          "JARVIS reasoning engine is over budget. Top up the Emergent LLM Key (Profile → Universal Key → Add Balance) and try again."
        );
        await speak("My reasoning engine is currently over its budget cap, sir. Please top up the universal key.");
      } else {
        setError(raw || "Something went wrong.");
        await speak("Apologies, sir. I encountered an error.");
      }
    }
  }, [speak, getContext, onAction, history, onBeforeIntent]);

  // Voice turn: record → compress (Safari-friendly small WAV) → transcribe →
  // process. If no speech is detected (user dwelling on a chart, thinking,
  // etc.) we silently restart the mic instead of nagging "could you repeat?".
  const runVoiceTurn = useCallback(async () => {
    // Capture sessionId so a tap-to-interrupt that fires mid-record /
    // mid-transcribe causes this whole turn to bail without spawning
    // its own follow-up listen kick (which would race the new one).
    const turnSession = sessionIdRef.current;
    const result = await recordCommand();
    if (!result || cancelledRef.current || sessionIdRef.current !== turnSession) return;
    const { blob, sawSpeech } = result;
    // VAD says the user never spoke — skip the Whisper round-trip entirely
    // (avoids prompt-text hallucinations like "Patients have French names")
    // and restart the mic silently.
    if (!sawSpeech || !blob || blob.size < 800) {
      if (!cancelledRef.current) {
        setStatus("listening");
        setTimeout(() => {
          if (!cancelledRef.current) runVoiceTurn();
        }, 100);
      }
      return;
    }
    setStatus("processing");
    try {
      // iOS Safari produces ~10 Mbps AAC files even with the bitrate hint
      // set, so we re-encode to 16 kHz mono PCM WAV (~32 KB/sec) before
      // upload. This typically shrinks 10 s of voice from ~12 MB → ~320 KB.
      const compressed = await compressVoiceBlob(blob);
      const form = new FormData();
      const filename = compressed.type === "audio/wav" ? "voice.wav" : "voice.webm";
      form.append("audio", compressed, filename);
      // Generous timeout so slow cellular connections don't fail before the
      // upload + Whisper round-trip completes.
      const trRes = await axios.post(`${API}/jarvis/transcribe`, form, {
        timeout: 60000,
      });
      // If an interrupt happened during the upload, drop the transcript.
      if (sessionIdRef.current !== turnSession) return;
      const text = (trRes.data && trRes.data.text) || "";
      // Successful round-trip — reset the failure counter.
      transcribeFailCountRef.current = 0;
      if (!text.trim()) {
        // No speech detected — just go back to listening silently. No
        // "didn't catch that, sir" nag. Lets the user study a chart for
        // as long as they like without JARVIS jabbering at them.
        if (!cancelledRef.current) {
          setStatus("listening");
          setTimeout(() => {
            if (!cancelledRef.current) runVoiceTurn();
          }, 100);
        }
        return;
      }
      await processTranscript(text);
    } catch (e) {
      console.error("JARVIS transcribe failed", e);
      const msg = e?.response?.data?.detail || e?.message || "Transcription failed.";
      const isTimeout = /timeout/i.test(msg);
      setError(
        isTimeout
          ? "Voice upload timed out (cellular network may be slow). Try a shorter command and tap ENGAGE."
          : msg
      );
      // Track consecutive failures so a broken Whisper / network doesn't
      // turn JARVIS into a parrot saying "apologies" every 5 seconds.
      transcribeFailCountRef.current = (transcribeFailCountRef.current || 0) + 1;
      if (transcribeFailCountRef.current >= 2) {
        // Stop the listen loop and surface a single clear error instead.
        cancelledRef.current = true;
        await speak(
          "Apologies, sir. My voice link appears to be down. Please dismiss me and tap the launcher to retry.",
        );
        setStatus("ready");
        return;
      }
      await speak("Apologies, sir. Voice transcription is unavailable. Please try again.");
    }
    // End-of-turn re-listen kick. Done HERE (not in a useEffect) because
    // Web Audio playback fires no DOM "ended" event.
    // If an interrupt happened anywhere in this turn (tap → bumped
    // sessionIdRef), interruptToListen already scheduled its own fresh
    // listen — DO NOT also queue one here, that would double up the
    // mic request and confuse iOS.
    if (sessionIdRef.current !== turnSession) return;
    if (
      modeRef.current === "voice"
      && !cancelledRef.current
      && !dismissAfterReplyRef.current
    ) {
      setTimeout(() => {
        if (!cancelledRef.current && !dismissAfterReplyRef.current) {
          runVoiceTurnRef.current?.();
        }
      }, 200);
    }
  }, [recordCommand, processTranscript, speak]);

  // Expose runVoiceTurn through the ref so speak()'s Web-Speech-fallback
  // path can kick the listen loop without a circular useCallback dep.
  useEffect(() => {
    runVoiceTurnRef.current = runVoiceTurn;
  }, [runVoiceTurn]);

  const sendTyped = useCallback(async () => {
    const text = typed.trim();
    if (!text) return;
    setTyped("");
    setError("");
    await processTranscript(text);
  }, [typed, processTranscript]);

  // Open in voice mode — start listening IMMEDIATELY so the user can
  // begin speaking the moment they tap the launcher. The hologram +
  // "LISTENING" status text is sufficient visual confirmation; we skip
  // the verbose spoken greeting to keep the experience snappy. (User
  // explicitly asked for "default to listening to my next command".)
  const openVoice = useCallback(async () => {
    // New session — every tap increments this. Any queued dismiss
    // close from the prior session will see a mismatched ID and bail.
    sessionIdRef.current += 1;
    const mySession = sessionIdRef.current;
    cancelledRef.current = false;
    dismissAfterReplyRef.current = false;
    transcribeFailCountRef.current = 0;
    // Prime the persistent <audio> element WITHIN this synchronous tap
    // handler so iOS Safari unlocks audio for the rest of the session.
    // Without this, every reply after the greeting silently fails.
    unlockAudio();
    setIsOpen(true);
    setMinimized(false);
    setMode("voice");
    setStatus("listening");
    setTranscript("");
    setReply("");
    setError("");
    setChartView(null);
    // Fresh conversation history — don't carry context from a prior
    // dismissed session (which might have ended on a farewell).
    setHistory([]);
    // Tiny pause so the panel + hologram have a frame to render before
    // the mic permission prompt fires — feels less jarring on iOS.
    await new Promise((r) => setTimeout(r, 60));
    if (sessionIdRef.current !== mySession) return;
    if (!cancelledRef.current) await runVoiceTurn();
  }, [runVoiceTurn, unlockAudio]);

  // Open in text mode — silent, no mic, no auto-greet audio.
  const openText = useCallback(() => {
    if (isOpen) return;
    cancelledRef.current = false;
    setIsOpen(true);
    setMinimized(false);
    setMode("text");
    setStatus("ready");
    setTranscript("");
    setReply("Good day, sir. How may I be of service?");
    setError("");
  }, [isOpen]);

  // Continuous voice loop: after each TTS reply, listen again until
  // cancelled. Driven directly from `speak()` resolution (instead of the
  // old <audio>.ended event listener) since we now use Web Audio API
  // playback — which has no DOM event. The kick lives at the bottom of
  // `runVoiceTurn` so it always fires after a complete turn.

  // Auto-open hook: when the host (e.g. DashboardView after a PDF upload)
  // bumps `autoOpenSignal`, fire openVoice() so JARVIS greets the user and
  // starts listening without requiring a tap. On iOS Safari this only
  // succeeds if a user-gesture is still pending — otherwise the audio play
  // and mic request will silently fail and the user will need to tap
  // ENGAGE manually. Tracked via a ref so we only fire on signal changes.
  const lastAutoOpenSignalRef = useRef(null);
  useEffect(() => {
    if (autoOpenSignal == null) return;
    if (lastAutoOpenSignalRef.current === autoOpenSignal) return;
    lastAutoOpenSignalRef.current = autoOpenSignal;
    if (isOpen) return; // already open — don't restart
    openVoice();
  }, [autoOpenSignal, isOpen, openVoice]);

  const closeJarvis = useCallback(() => {
    cancelledRef.current = true;
    dismissAfterReplyRef.current = false;
    setIsOpen(false);
    setMinimized(false);
    setHistory([]);
    setStatus("idle");
    setTyped("");
    setChartView(null);
    try { mediaRecorderRef.current?.stop(); } catch (_) { /* noop */ }
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) { /* noop */ }
    try { audioContextRef.current?.close(); } catch (_) { /* noop */ }
    try { audioElRef.current?.pause(); } catch (_) { /* noop */ }
    // Stop any in-flight Web Audio playback — closing the context here
    // is too aggressive (would re-trigger iOS autoplay restrictions next
    // session); just halt the active source if any.
    try {
      if (currentSourceRef.current) {
        currentSourceRef.current.stop();
        currentSourceRef.current = null;
      }
    } catch (_) { /* noop */ }
  }, []);

  // Tap-to-interrupt: when the user taps the JARVIS HUD area, immediately
  // STOP whatever JARVIS is doing (mid-speech, mid-processing, mid-
  // anything) and start a fresh listen turn. This is what the user means
  // by "I want to control him". Implementation:
  //   1. Bump sessionIdRef so any inflight async (axios intent fetch,
  //      pending speak resolution) sees a mismatched ID and bails out
  //      before calling onAction/speak again.
  //   2. Hard-stop the current Web Audio source so TTS playback halts.
  //   3. Stop the active MediaRecorder + mic stream so the previous
  //      listen-turn's onstop callback doesn't fire with stale audio.
  //   4. Cancel the RAF loops (voice-reactive pump, VAD silence timer).
  //   5. Reset flags so the new turn runs cleanly.
  //   6. Call runVoiceTurn() to begin a fresh listen IMMEDIATELY.
  const interruptToListen = useCallback(() => {
    if (!isOpen) return;
    // 1. Invalidate any inflight callbacks from the prior turn.
    sessionIdRef.current += 1;
    // 2. Stop active TTS playback.
    try {
      if (currentSourceRef.current) {
        currentSourceRef.current.stop();
        currentSourceRef.current = null;
      }
    } catch (_) { /* noop */ }
    // Also cancel any Web Speech fallback that might be mid-utterance.
    try {
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    } catch (_) { /* noop */ }
    // 3. Stop active mic capture (without firing its normal "stopped"
    // promise resolution — bumping sessionIdRef already invalidates).
    try { mediaRecorderRef.current?.stop(); } catch (_) { /* noop */ }
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_) { /* noop */ }
    streamRef.current = null;
    try { audioContextRef.current?.close(); } catch (_) { /* noop */ }
    audioContextRef.current = null;
    // 4. Cancel animation frames.
    try { cancelAnimationFrame(voiceRafRef.current); } catch (_) { /* noop */ }
    try { cancelAnimationFrame(silenceTimerRef.current); } catch (_) { /* noop */ }
    // Reset --jarvis-level so the orb stops mid-pulse cleanly.
    if (orbWrapperRef.current) {
      orbWrapperRef.current.style.setProperty("--jarvis-level", "0");
    }
    // 5. Reset flags.
    cancelledRef.current = false;
    dismissAfterReplyRef.current = false;
    transcribeFailCountRef.current = 0;
    setError("");
    // 6. Jump straight into a fresh listen turn on the next tick so the
    // stop() calls above flush before the new mic request goes out.
    setStatus("listening");
    setTimeout(() => {
      if (!cancelledRef.current) runVoiceTurnRef.current?.();
    }, 60);
  }, [isOpen]);

  // Status-aware panel tap handler. The user explicitly wants two
  // different behaviours depending on what JARVIS is currently doing:
  //   - LISTENING → finalize the current recording NOW (don't restart).
  //     The existing recordCommand `await stopped` promise resolves
  //     naturally, the blob feeds into Whisper, and the rest of the
  //     pipeline (intent → speak → re-listen) runs unchanged. This is
  //     what "Jarvis just keeps listening and does not stop" was asking
  //     for: a hard "I'm done, process now" button when the VAD silence
  //     detector hasn't tripped (e.g. noisy room).
  //   - SPEAKING / PROCESSING / ERROR / READY → interrupt and restart a
  //     fresh listen (the existing interruptToListen flow).
  // mediaRecorder.stop() is idempotent, so a tap that races with the
  // VAD silence-detector calling stop() too is safe — second call is a
  // no-op.
  const handlePanelTap = useCallback(() => {
    if (!isOpen) return;
    if (status === "listening") {
      try {
        if (
          mediaRecorderRef.current
          && mediaRecorderRef.current.state === "recording"
        ) {
          mediaRecorderRef.current.stop();
        }
      } catch (e) { console.warn("Failed to finalize recording on tap", e); }
      return;
    }
    interruptToListen();
  }, [isOpen, status, interruptToListen]);

  const statusLabel = {
    idle: "STANDBY",
    greeting: "INITIALISING…",
    listening: "LISTENING… (TAP TO STOP)",
    processing: "ANALYSING…",
    speaking: "TRANSMITTING… (TAP TO INTERRUPT)",
    ready: "READY · TAP TO SPEAK",
    error: "ERROR",
  }[status];

  // Map status → orb animation class (defined in <style> below).
  const orbState = status === "listening"
    ? "jarvis-orb-listen"
    : status === "speaking"
      ? "jarvis-orb-speak"
      : status === "processing"
        ? "jarvis-orb-process"
        : status === "error"
          ? "jarvis-orb-error"
          : "jarvis-orb-idle";

  return (
    <>
      {/* Inline keyframes / glow primitives — scoped via class names so they
          don't leak globally. */}
      <style>{`
        @keyframes jarvis-breathe {
          0%, 100% { transform: scale(0.94); opacity: 0.78; }
          50%      { transform: scale(1.04); opacity: 1; }
        }
        @keyframes jarvis-pulse-fast {
          0%, 100% { transform: scale(0.88); opacity: 1; }
          50%      { transform: scale(1.10); opacity: 0.55; }
        }
        @keyframes jarvis-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes jarvis-ring-ripple {
          0%   { transform: scale(0.5); opacity: 0.85; }
          100% { transform: scale(1.85); opacity: 0; }
        }
        @keyframes jarvis-scan {
          0%   { transform: translateY(-100%); opacity: 0; }
          10%  { opacity: 0.6; }
          90%  { opacity: 0.6; }
          100% { transform: translateY(220%); opacity: 0; }
        }
        @keyframes jarvis-flicker {
          0%, 100% { opacity: 1; }
          47%      { opacity: 0.85; }
          50%      { opacity: 0.6; }
          53%      { opacity: 0.95; }
        }
        .jarvis-glow-text {
          color: #BEEBFF;
          text-shadow:
            0 0 6px rgba(0, 229, 255, 0.55),
            0 0 14px rgba(0, 180, 255, 0.32);
          font-family: "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
          letter-spacing: 0.05em;
        }
        .jarvis-grid {
          background-image:
            linear-gradient(rgba(0, 229, 255, 0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 229, 255, 0.06) 1px, transparent 1px);
          background-size: 22px 22px;
          background-position: center center;
        }
        .jarvis-orb-core {
          position: absolute;
          inset: 22%;
          border-radius: 9999px;
          background: radial-gradient(circle at 35% 35%, #C8F4FF 0%, #4FD2FF 28%, #0095D8 60%, #003E73 100%);
          box-shadow:
            inset 0 0 18px rgba(255,255,255,0.55),
            0 0 32px rgba(0, 229, 255, 0.65),
            0 0 60px rgba(0, 180, 255, 0.45);
        }
        .jarvis-orb-ring {
          position: absolute;
          inset: 6%;
          border-radius: 9999px;
          border: 1px solid rgba(0, 229, 255, 0.45);
          box-shadow: 0 0 18px rgba(0, 229, 255, 0.35), inset 0 0 12px rgba(0, 229, 255, 0.25);
        }
        .jarvis-orb-ring-outer {
          position: absolute;
          inset: -8%;
          border-radius: 9999px;
          border: 1px dashed rgba(0, 229, 255, 0.35);
        }
        .jarvis-ripple {
          position: absolute;
          inset: 0;
          border-radius: 9999px;
          border: 1px solid rgba(0, 229, 255, 0.55);
        }
        .jarvis-orb-idle .jarvis-orb-core    { animation: jarvis-breathe 3.8s ease-in-out infinite; }
        .jarvis-orb-listen .jarvis-orb-core  { animation: jarvis-pulse-fast 0.85s ease-in-out infinite; box-shadow: 0 0 36px rgba(255, 90, 90, 0.75), inset 0 0 18px rgba(255,255,255,0.5); background: radial-gradient(circle at 35% 35%, #FFD9D9 0%, #FF6B6B 28%, #C03030 60%, #5A0A0A 100%); }
        .jarvis-orb-listen .jarvis-orb-ring  { border-color: rgba(255, 110, 110, 0.6); box-shadow: 0 0 22px rgba(255, 110, 110, 0.5); }
        .jarvis-orb-listen .jarvis-ripple    { animation: jarvis-ring-ripple 1.1s ease-out infinite; border-color: rgba(255, 110, 110, 0.65); }
        .jarvis-orb-speak .jarvis-orb-core   { animation: jarvis-breathe 0.9s ease-in-out infinite; }
        .jarvis-orb-speak .jarvis-ripple     { animation: jarvis-ring-ripple 1.4s ease-out infinite; }
        .jarvis-orb-process .jarvis-orb-ring-outer { animation: jarvis-spin 2.2s linear infinite; }
        .jarvis-orb-process .jarvis-orb-core { animation: jarvis-breathe 1.4s ease-in-out infinite; }
        .jarvis-orb-error .jarvis-orb-core   { animation: jarvis-flicker 1s ease-in-out infinite; background: radial-gradient(circle at 35% 35%, #FFE2B5 0%, #FF9540 30%, #C04A00 65%, #4A1500 100%); box-shadow: 0 0 28px rgba(255, 149, 64, 0.6); }
        .jarvis-scan-line {
          position: absolute;
          left: 0; right: 0;
          height: 35%;
          background: linear-gradient(to bottom, transparent, rgba(0, 229, 255, 0.15), transparent);
          animation: jarvis-scan 3.2s linear infinite;
          pointer-events: none;
        }
        .jarvis-launch-orb {
          background: radial-gradient(circle at 35% 35%, #C8F4FF 0%, #4FD2FF 28%, #0095D8 60%, #003E73 100%);
          box-shadow:
            inset 0 0 12px rgba(255,255,255,0.45),
            0 0 18px rgba(0, 229, 255, 0.55),
            0 6px 22px rgba(0, 120, 200, 0.45);
          animation: jarvis-breathe 3.5s ease-in-out infinite;
        }

        /* ==== Futuristic AI Sphere ==========================================
           A multi-layered, audio-reactive sphere with iridescent halo,
           counter-rotating plasma swirl, morphing liquid core, and floating
           specular shimmers. Drops in where the small static orb used to
           live (open panel center). Driven by the same orbState classes
           (.jarvis-orb-idle/listen/speak/process/error) so it reacts to
           voice activity automatically.                                    */
        .jarvis-sphere-wrap {
          position: relative;
          width: 100%; height: 100%;
          display: flex; align-items: center; justify-content: center;
          isolation: isolate;
        }
        /* Outer iridescent halo — Siri-style warm gradient (orange → coral
           → magenta → violet → cyan) that slowly rotates AND shifts hue.
           Stays in this palette across all states; listening/speaking only
           change the speed, never the color. */
        .jarvis-sphere-halo {
          position: absolute;
          inset: -30%;
          border-radius: 9999px;
          background: conic-gradient(
            from 0deg,
            rgba(255, 138, 61, 0.92) 0deg,
            rgba(255, 77, 200, 0.88) 90deg,
            rgba(201, 64, 255, 0.85) 180deg,
            rgba(123, 92, 255, 0.85) 250deg,
            rgba(0, 200, 255, 0.78) 310deg,
            rgba(255, 138, 61, 0.92) 360deg
          );
          filter: blur(22px) saturate(160%);
          opacity: 0.9;
          animation: jarvis-spin 16s linear infinite, jarvis-hue 28s linear infinite;
          z-index: 0;
        }
        /* Plasma swirl — second conic layer rotating the other way,
           masked into a thin ring with radial-gradient alpha. */
        .jarvis-sphere-plasma {
          position: absolute;
          inset: 0;
          border-radius: 9999px;
          background:
            conic-gradient(
              from 180deg,
              rgba(255, 255, 255, 0.0) 0deg,
              rgba(255, 255, 255, 0.55) 30deg,
              rgba(0, 229, 255, 0.45) 90deg,
              rgba(180, 90, 255, 0.55) 160deg,
              rgba(0, 229, 255, 0.45) 230deg,
              rgba(255, 255, 255, 0.55) 320deg,
              rgba(255, 255, 255, 0.0) 360deg
            );
          -webkit-mask: radial-gradient(circle at center, transparent 35%, black 50%, black 78%, transparent 92%);
                  mask: radial-gradient(circle at center, transparent 35%, black 50%, black 78%, transparent 92%);
          mix-blend-mode: screen;
          filter: blur(2px);
          animation: jarvis-spin 9s linear infinite reverse;
          z-index: 1;
        }
        /* Liquid core — morphing radial-gradient sphere. The border-radius
           morphs slightly to break the perfect-circle feel and reads as a
           living blob of light. */
        .jarvis-sphere-core {
          position: absolute;
          inset: 18%;
          border-radius: 60% 40% 55% 45% / 50% 60% 40% 50%;
          background:
            radial-gradient(circle at 30% 25%, rgba(255,255,255,0.95) 0%, rgba(180, 235, 255, 0.85) 18%, rgba(60, 180, 255, 0.85) 42%, rgba(20, 80, 200, 0.9) 68%, rgba(2, 18, 60, 0.95) 100%);
          box-shadow:
            inset -10px -14px 30px rgba(2, 18, 60, 0.55),
            inset 14px 18px 30px rgba(255, 255, 255, 0.35),
            0 0 28px rgba(0, 229, 255, 0.7),
            0 0 60px rgba(80, 130, 255, 0.45);
          animation: jarvis-morph 7s ease-in-out infinite, jarvis-breathe 4.6s ease-in-out infinite;
          z-index: 2;
        }
        /* A second core layer with reversed gradient + screen blend creates
           an internal swirling shimmer like light moving inside the sphere. */
        .jarvis-sphere-core::before {
          content: "";
          position: absolute;
          inset: 8%;
          border-radius: inherit;
          background:
            radial-gradient(circle at 70% 75%, rgba(255, 180, 255, 0.0) 30%, rgba(180, 90, 255, 0.55) 55%, rgba(0, 229, 255, 0.0) 80%),
            radial-gradient(circle at 30% 80%, rgba(0, 229, 255, 0.35) 0%, rgba(0, 229, 255, 0.0) 60%);
          mix-blend-mode: screen;
          animation: jarvis-spin 8s linear infinite, jarvis-morph 9s ease-in-out infinite reverse;
        }
        /* Specular highlight — small bright white glint that drifts. */
        .jarvis-sphere-core::after {
          content: "";
          position: absolute;
          top: 18%; left: 22%;
          width: 22%; height: 18%;
          border-radius: 9999px;
          background: radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 70%);
          filter: blur(2px);
          animation: jarvis-glint 5.5s ease-in-out infinite;
        }
        @keyframes jarvis-morph {
          0%, 100% { border-radius: 60% 40% 55% 45% / 50% 60% 40% 50%; }
          25%      { border-radius: 45% 55% 60% 40% / 55% 45% 50% 55%; }
          50%      { border-radius: 50% 50% 45% 55% / 60% 50% 55% 45%; }
          75%      { border-radius: 55% 45% 50% 50% / 45% 55% 60% 40%; }
        }
        @keyframes jarvis-hue {
          0%   { filter: blur(14px) saturate(140%) hue-rotate(0deg);   }
          100% { filter: blur(14px) saturate(140%) hue-rotate(360deg); }
        }
        @keyframes jarvis-glint {
          0%, 100% { transform: translate(0, 0)        scale(1);    opacity: 0.95; }
          50%      { transform: translate(8px, 6px)    scale(0.85); opacity: 0.55; }
        }
        /* State variants — match the orbState classes already set on the
           wrapper, so voice activity drives the sphere automatically.
           Halo palette stays warm (orange → pink → violet) across ALL
           states; only the animation speed and core pulse change. */
        .jarvis-orb-listen .jarvis-sphere-halo {
          /* Same gradient, faster rotation + hue cycle. */
          animation: jarvis-spin 7s linear infinite, jarvis-hue 16s linear infinite;
          opacity: 0.95;
        }
        .jarvis-orb-listen .jarvis-sphere-core {
          animation: jarvis-morph 3s ease-in-out infinite, jarvis-breathe 1.1s ease-in-out infinite;
        }
        .jarvis-orb-speak .jarvis-sphere-core {
          animation: jarvis-morph 4s ease-in-out infinite, jarvis-breathe 0.9s ease-in-out infinite;
        }
        .jarvis-orb-speak .jarvis-sphere-halo {
          animation: jarvis-spin 9s linear infinite, jarvis-hue 12s linear infinite;
        }
        .jarvis-orb-process .jarvis-sphere-halo {
          animation: jarvis-spin 4.5s linear infinite, jarvis-hue 10s linear infinite;
        }
        .jarvis-orb-error .jarvis-sphere-halo {
          /* Keep the warm palette but flicker on error */
          animation: jarvis-spin 18s linear infinite, jarvis-flicker 1.4s ease-in-out infinite;
        }
        .jarvis-orb-error .jarvis-sphere-core {
          animation: jarvis-flicker 1s ease-in-out infinite;
        }

        /* ==== JARVIS Plexus Orb (canvas particle-network sphere) ===
           Matches the user's TikTok reference: a sphere of ~140 glowing
           particles connected by glowing cyan lines that shimmer in 3D.
           All rendering is done inside the canvas by the JarvisHologram
           component — these styles just size the canvas. */
        .jarvis-voice-reactive { --jarvis-level: 0; }
        .jarvis-holo-wrap { position: relative; width: 100%; height: 100%; }
        .jarvis-orb-stage {
          position: relative;
          width: 100%;
          height: 100%;
          display: block;
        }
        .jarvis-plexus-canvas {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          /* Outer glow halo around the entire mesh — voice level boosts
             it so the sphere brightens when the speaker is loud. */
          filter:
            drop-shadow(0 0 calc(8px + var(--jarvis-level) * 18px) rgba(0, 229, 255, 0.55))
            drop-shadow(0 0 calc(2px + var(--jarvis-level) * 10px) rgba(255, 255, 255, 0.35));
          transition: filter 80ms linear;
        }
        .jarvis-orb-listen .jarvis-plexus-canvas {
          filter:
            drop-shadow(0 0 calc(8px + var(--jarvis-level) * 18px) rgba(255, 110, 110, 0.55))
            drop-shadow(0 0 calc(2px + var(--jarvis-level) * 10px) rgba(255, 255, 255, 0.35));
        }
        .jarvis-orb-error .jarvis-plexus-canvas {
          filter:
            drop-shadow(0 0 12px rgba(255, 149, 64, 0.7))
            drop-shadow(0 0 4px rgba(255, 255, 255, 0.35));
        }

        /* (Legacy CSS-orb rules — ripple-ring, spokes, bars, sphere,
            highlight, state tints — removed; the canvas renders all of
            it now via the JarvisHologram component above.) */


        /* HUD launch composition — rotating outer ring + counter-rotating
           segments + faint constellation dots. Gives the floating mic
           button a real "arc reactor / call JARVIS" feel before tap. */
        .jarvis-launch-ring-outer {
          position: absolute;
          inset: -6px;
          border-radius: 9999px;
          border: 1px solid rgba(0, 229, 255, 0.35);
          box-shadow: 0 0 14px rgba(0, 229, 255, 0.25), inset 0 0 10px rgba(0, 229, 255, 0.12);
          animation: jarvis-spin 9s linear infinite;
        }
        .jarvis-launch-ring-outer::before,
        .jarvis-launch-ring-outer::after {
          content: "";
          position: absolute;
          inset: -1px;
          border-radius: 9999px;
          border: 1px solid transparent;
          /* Two opposing quarter-arcs in cyan */
          border-top-color: rgba(0, 229, 255, 0.85);
          border-right-color: rgba(0, 229, 255, 0.85);
        }
        .jarvis-launch-ring-outer::after {
          transform: rotate(180deg);
          border-top-color: rgba(190, 235, 255, 0.55);
          border-right-color: rgba(190, 235, 255, 0.55);
        }
        .jarvis-launch-ring-inner {
          position: absolute;
          inset: 2px;
          border-radius: 9999px;
          border: 1px dashed rgba(0, 229, 255, 0.55);
          animation: jarvis-spin 14s linear infinite reverse;
        }
        .jarvis-launch-status {
          position: absolute;
          top: 100%;
          left: 50%;
          margin-top: 8px;
          transform: translateX(-50%);
          font-family: "JetBrains Mono", ui-monospace, monospace;
          font-size: 8px;
          font-weight: 700;
          letter-spacing: 0.22em;
          color: #BEEBFF;
          text-shadow: 0 0 6px rgba(0, 229, 255, 0.6);
          white-space: nowrap;
          animation: jarvis-flicker 4s ease-in-out infinite;
          pointer-events: none;
        }
        /* HUD corner brackets — L-shaped markers in the four corners of
           the open JARVIS panel to sell the "tactical readout" aesthetic. */
        .jarvis-hud-corner {
          position: absolute;
          width: 14px;
          height: 14px;
          pointer-events: none;
        }
        .jarvis-hud-corner::before,
        .jarvis-hud-corner::after {
          content: "";
          position: absolute;
          background: rgba(0, 229, 255, 0.85);
          box-shadow: 0 0 6px rgba(0, 229, 255, 0.65);
        }
        .jarvis-hud-corner::before {
          /* horizontal stroke */
          width: 100%; height: 1.5px; left: 0;
        }
        .jarvis-hud-corner::after {
          /* vertical stroke */
          height: 100%; width: 1.5px; top: 0;
        }
        .jarvis-hud-corner.tl { top: 8px; left: 8px; }
        .jarvis-hud-corner.tl::before { top: 0; }
        .jarvis-hud-corner.tl::after  { left: 0; }
        .jarvis-hud-corner.tr { top: 8px; right: 8px; }
        .jarvis-hud-corner.tr::before { top: 0; }
        .jarvis-hud-corner.tr::after  { right: 0; }
        .jarvis-hud-corner.bl { bottom: 8px; left: 8px; }
        .jarvis-hud-corner.bl::before { bottom: 0; }
        .jarvis-hud-corner.bl::after  { left: 0; }
        .jarvis-hud-corner.br { bottom: 8px; right: 8px; }
        .jarvis-hud-corner.br::before { bottom: 0; }
        .jarvis-hud-corner.br::after  { right: 0; }
      `}</style>

      {/* Floating launcher — reactor orb (voice) + small text-mode pill above. */}
      {!isOpen ? (
        <div
          className="fixed z-40 flex flex-col items-end gap-2"
          style={{
            right: "calc(env(safe-area-inset-right, 0px) + 16px)",
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)",
          }}
        >
          {/* iframe hint: iOS Safari blocks mic in iframes, so prompt the user
              to open the app in a real Safari tab where voice will work. */}
          {inIframe ? (
            <a
              href={directUrl}
              target="_top"
              rel="noopener"
              data-testid="jarvis-open-in-safari"
              className="h-7 px-2.5 rounded-full text-[9px] font-bold uppercase tracking-[0.18em] flex items-center gap-1 transition-all"
              style={{
                background: "rgba(40, 12, 0, 0.92)",
                color: "#FFCFA8",
                border: "1px solid rgba(255, 149, 64, 0.5)",
                boxShadow: "0 0 10px rgba(255, 149, 64, 0.3)",
                fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                textShadow: "0 0 4px rgba(255, 149, 64, 0.4)",
              }}
              title="Voice doesn't work inside this preview iframe — tap to open in a real Safari tab"
            >
              ↗ FOR VOICE
            </a>
          ) : null}
          {/* Text-mode entry hidden for now — JARVIS is smart enough that
              voice-only feels cleaner. Restore this block to bring it back. */}
          {false ? (
          <button
            type="button"
            onClick={openText}
            data-testid="jarvis-open-text"
            aria-label="Open JARVIS — type a command"
            className="h-9 px-3 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] flex items-center gap-1.5 transition-all"
            style={{
              background: "rgba(4, 12, 22, 0.92)",
              color: "#BEEBFF",
              border: "1px solid rgba(0, 229, 255, 0.45)",
              boxShadow: "0 0 14px rgba(0, 229, 255, 0.25), inset 0 0 8px rgba(0, 229, 255, 0.15)",
              fontFamily: '"JetBrains Mono", ui-monospace, monospace',
              textShadow: "0 0 6px rgba(0, 229, 255, 0.55)",
            }}
          >
            <Keyboard className="h-3.5 w-3.5" strokeWidth={2} />
            TYPE
          </button>
          ) : null}
          <button
            type="button"
            onClick={openVoice}
            data-testid="jarvis-open"
            aria-label="Open JARVIS voice assistant"
            className="relative h-16 w-16 rounded-full flex items-center justify-center jarvis-launch-holo"
          >
            <span className="jarvis-launch-ring-outer" />
            <span className="jarvis-launch-ring-inner" />
            {/* Mini JARVIS hologram replaces the static mic icon — same
                animated HUD as the full panel, scaled to fit the launcher. */}
            <span className="absolute inset-[6%] pointer-events-none">
              <JarvisHologram />
            </span>
            <span className="jarvis-launch-status">TAP TO ENGAGE</span>
          </button>
        </div>
      ) : null}

      {/* Minimized — small floating orb showing JARVIS is still listening,
          but unblocked so the user can see and interact with whatever
          section the dashboard navigated to. Tap to re-expand. */}
      {isOpen && minimized ? (
        <button
          type="button"
          onClick={() => setMinimized(false)}
          data-testid="jarvis-expand"
          aria-label="Re-open JARVIS"
          className="fixed z-50 flex items-center gap-2 px-3 py-2 rounded-full"
          style={{
            right: "calc(env(safe-area-inset-right, 0px) + 16px)",
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)",
            background: "rgba(4, 12, 22, 0.92)",
            border: "1px solid rgba(0, 229, 255, 0.45)",
            boxShadow: "0 0 22px rgba(0, 229, 255, 0.35), inset 0 0 8px rgba(0, 229, 255, 0.15)",
            backdropFilter: "blur(8px)",
          }}
        >
          <span
            className="relative inline-block"
            style={{ width: 22, height: 22 }}
          >
            <span className="jarvis-orb-ring" style={{ inset: 0 }} />
            <span
              className="jarvis-orb-core"
              style={{
                inset: "25%",
                animation: "jarvis-breathe 2.6s ease-in-out infinite",
              }}
            />
          </span>
          <span
            className="text-[10px] font-bold uppercase tracking-[0.2em]"
            style={{
              color: "#BEEBFF",
              fontFamily: '"JetBrains Mono", ui-monospace, monospace',
              textShadow: "0 0 6px rgba(0, 229, 255, 0.6)",
            }}
          >
            JARVIS
          </span>
        </button>
      ) : null}

      {isOpen && !minimized ? (
        <div
          className="fixed z-50 w-[min(94vw,360px)] rounded-2xl overflow-hidden"
          style={{
            right: "calc(env(safe-area-inset-right, 0px) + 12px)",
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)",
            background: "linear-gradient(180deg, #040810 0%, #06121E 60%, #050C16 100%)",
            border: "1px solid rgba(0, 229, 255, 0.35)",
            boxShadow:
              "0 24px 60px rgba(0, 0, 0, 0.55), 0 0 32px rgba(0, 180, 255, 0.18), inset 0 0 0 1px rgba(0, 229, 255, 0.06)",
          }}
          data-testid="jarvis-panel"
        >
          {/* HUD tactical-readout corner brackets — sells the JARVIS frame. */}
          <span className="jarvis-hud-corner tl" />
          <span className="jarvis-hud-corner tr" />
          <span className="jarvis-hud-corner bl" />
          <span className="jarvis-hud-corner br" />
          {/* Header strip with status. */}
          <div
            className="relative px-4 py-2.5 flex items-center justify-between"
            style={{
              background: "linear-gradient(180deg, rgba(0,30,55,0.95) 0%, rgba(0,18,35,0.95) 100%)",
              borderBottom: "1px solid rgba(0, 229, 255, 0.25)",
            }}
          >
            <div className="flex items-center gap-2">
              <div
                className="h-1.5 w-1.5 rounded-full"
                style={{
                  background: status === "listening" ? "#FF6B6B" : status === "error" ? "#FF9540" : "#00E5FF",
                  boxShadow:
                    status === "listening"
                      ? "0 0 8px #FF6B6B"
                      : status === "error"
                        ? "0 0 8px #FF9540"
                        : "0 0 8px #00E5FF",
                }}
              />
              <div
                className="text-[10px] font-bold uppercase tracking-[0.22em]"
                style={{
                  color: "#BEEBFF",
                  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                  textShadow: "0 0 6px rgba(0, 229, 255, 0.5)",
                }}
              >
                J.A.R.V.I.S
              </div>
              {mode === "text" ? (
                <div
                  className="text-[9px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded"
                  style={{
                    color: "#7FD9FF",
                    background: "rgba(0, 100, 160, 0.25)",
                    border: "1px solid rgba(0, 200, 255, 0.35)",
                    fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                  }}
                >
                  TXT
                </div>
              ) : null}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setTtsMuted((m) => !m)}
                data-testid="jarvis-tts-toggle"
                aria-label={ttsMuted ? "Unmute voice replies" : "Mute voice replies"}
                title={ttsMuted ? "Voice replies muted" : "Voice replies on"}
                className="h-6 w-6 rounded transition-colors flex items-center justify-center"
                style={{ color: "#BEEBFF" }}
              >
                {ttsMuted ? (
                  <VolumeX className="h-3.5 w-3.5" strokeWidth={2} />
                ) : (
                  <Volume2 className="h-3.5 w-3.5" strokeWidth={2} />
                )}
              </button>
              <button
                type="button"
                onClick={() => setMinimized(true)}
                data-testid="jarvis-minimize"
                aria-label="Minimize JARVIS"
                title="Minimize so you can see the dashboard"
                className="h-6 w-6 rounded transition-colors flex items-center justify-center"
                style={{ color: "#BEEBFF" }}
              >
                <Minus className="h-3.5 w-3.5" strokeWidth={2} />
              </button>
              {/* (Close X removed — say "goodbye", "that will be all",
                  or "exit" verbally and JARVIS will dismiss himself.) */}
            </div>
          </div>

          {/* Reactor orb + status text. Once a chart is loaded it STAYS
              visible across all subsequent turns (sticky) — the hologram
              only returns when the user dismisses the chart via the BACK
              button or via JARVIS himself closing the panel.
              TAP-TO-INTERRUPT: tapping anywhere inside this area (or the
              transcript area below) hard-stops whatever JARVIS is doing
              and switches him to listening — see interruptToListen() for
              the full cancellation chain. */}
          <div
            className="relative px-4 pt-5 pb-3 jarvis-grid overflow-hidden cursor-pointer"
            style={{ background: "linear-gradient(180deg, #02060C 0%, #04101C 100%)" }}
            onClick={handlePanelTap}
            data-testid="jarvis-interrupt-zone"
            role="button"
            aria-label="Tap to finalize speech or interrupt JARVIS"
          >
            <div className="jarvis-scan-line" />
            {chartView ? (
              <div
                data-testid="jarvis-chart-view"
                className="relative mx-auto"
                style={{ width: "100%" }}
              >
                {/* BACK pill sits in its own header row above the chart, so
                    it never collides with the chart title or axis labels. */}
                <div className="flex items-center justify-between px-2 pb-2">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setChartView(null); }}
                    data-testid="jarvis-chart-back"
                    aria-label="Close chart and return to hologram"
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[9px] font-bold uppercase tracking-[0.18em]"
                    style={{
                      background: "rgba(4, 12, 22, 0.92)",
                      color: "#BEEBFF",
                      border: "1px solid rgba(0, 229, 255, 0.45)",
                      boxShadow: "0 0 10px rgba(0, 229, 255, 0.3)",
                      fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                      textShadow: "0 0 4px rgba(0, 229, 255, 0.5)",
                    }}
                  >
                    ← BACK
                  </button>
                  <span
                    className="text-[9px] uppercase tracking-[0.18em] opacity-70"
                    style={{
                      color: "#7FD9FF",
                      fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                    }}
                  >
                    HUD CHART
                  </span>
                </div>
                <div
                  className="px-1"
                  style={{ width: "100%", height: 280 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <JarvisChart spec={chartView} />
                </div>
              </div>
            ) : (
              <div className="relative mx-auto flex items-center justify-center" style={{ width: 240, height: 240 }}>
                <div ref={orbWrapperRef} className={`jarvis-holo-wrap jarvis-voice-reactive ${orbState}`}>
                  <JarvisHologram />
                </div>
              </div>
            )}
            <div
              className="mt-3 text-center text-[9px] font-bold uppercase tracking-[0.32em]"
              style={{
                color: "#7FD9FF",
                fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                textShadow: "0 0 6px rgba(0, 229, 255, 0.55)",
              }}
            >
              {statusLabel}
            </div>
          </div>

          {/* Transcript / reply panel — holographic text on dark.
              Tap here also interrupts JARVIS and switches to listening. */}
          <div
            className="px-4 py-3 space-y-2 max-h-[40vh] overflow-y-auto cursor-pointer"
            style={{
              background: "rgba(2, 8, 16, 0.92)",
              borderTop: "1px solid rgba(0, 229, 255, 0.18)",
              borderBottom: "1px solid rgba(0, 229, 255, 0.18)",
            }}
            onClick={handlePanelTap}
            data-testid="jarvis-interrupt-zone-text"
            role="button"
            aria-label="Tap to finalize speech or interrupt JARVIS"
          >
            {/* (User transcript echo removed per user request — only
                JARVIS's replies are shown on screen now.) */}
            {reply ? (
              <div className="text-sm leading-relaxed jarvis-glow-text">
                <span
                  className="text-[10px] font-bold uppercase tracking-[0.18em] mr-1.5"
                  style={{ color: "#00E5FF" }}
                >
                  JARVIS ›
                </span>
                {reply}
              </div>
            ) : null}
            {error ? (
              <div
                className="text-[10px] rounded px-2.5 py-2 space-y-2"
                style={{
                  color: "#FFB585",
                  background: "rgba(80, 30, 0, 0.45)",
                  border: "1px solid rgba(255, 149, 64, 0.45)",
                  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
                }}
              >
                <div>{error}</div>
                {inIframe ? (
                  <a
                    href={directUrl}
                    target="_top"
                    rel="noopener"
                    data-testid="jarvis-error-open-in-safari"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded font-bold uppercase tracking-[0.15em] transition-colors"
                    style={{
                      color: "#FFE5C8",
                      background: "rgba(255, 149, 64, 0.25)",
                      border: "1px solid rgba(255, 149, 64, 0.55)",
                    }}
                  >
                    ↗ Open in Safari
                  </a>
                ) : null}
              </div>
            ) : null}
            {!reply && !error && status === "greeting" ? (
              <div className="text-[10px] italic jarvis-glow-text">Booting subsystems…</div>
            ) : null}
          </div>

          {/* (Typed-command input removed — voice-only mode.) */}
          {/* (ENGAGE / END bottom toolbar removed — JARVIS listens
              continuously and closes on verbal dismiss commands like
              "goodbye", "that will be all", "exit". User can re-open
              by tapping the launcher hologram.) */}
        </div>
      ) : null}
    </>
  );
}
